/*
 * auth (utils)
 */
import { supabase } from "@psychology/auth/client"
import { toast } from "@psychology/react-ui"
import type { UserRole } from "@psychology/types"
import {
	isAdmin,
	getPlatformMetadata,
	isDebug,
	getUserRole,
	isOnWaitlist,
} from "@psychology/utils"
import { redirect } from "@tanstack/react-router"

type AuthPageLoaderOptions = {
	allowUnauthenticated?: boolean
	allowUnauthorized?: boolean
	redirectAuthorized?: string
}

export const authPageLoaderSoft =
	({
		allowUnauthenticated,
		allowUnauthorized,
		redirectAuthorized,
	}: AuthPageLoaderOptions) =>
	async () => {
		const { data, error } = await getAuthSession()
		if (error) {
			console.error("Auth getSession Error", { error })
		}

		let authenticated = true
		let authorized = true

		if (!data.session) {
			if (!allowUnauthenticated) {
				throw redirect({ to: "/auth" })
			}
			authenticated = false
		} else if (!getPlatformMetadata(data.session.user)) {
			if (!allowUnauthorized) {
				throw redirect({ to: "/waitlist" })
			}
			authorized = false
		} else if (redirectAuthorized) {
			throw redirect({ to: redirectAuthorized })
		}

		if (isDebug) {
			console.log("User", data.session?.user)
		}

		return {
			authenticated,
			authorized,
			user: data.session?.user,
			token: data.session?.access_token,
			refresh: refreshSession,
		}
	}

export async function authPageLoader() {
	const { data, error } = await getAuthSession()
	if (error) {
		console.error("Auth getSession Error", { error })
	}

	if (!data.session) {
		throw redirect({ to: "/auth" })
	} else if (!getPlatformMetadata(data.session.user)) {
		throw redirect({ to: "/waitlist" })
	}

	if (isDebug) {
		console.log("User", data.session.user)
	}

	return {
		user: data.session.user,
		token: data.session.access_token,
		refresh: refreshSession,
	}
}

export async function adminPageLoader() {
	const { data, error } = await getAuthSession()
	if (error) {
		console.error("Auth getSession Error", { error })
	}

	if (!data.session) {
		throw redirect({ to: "/auth" })
	} else if (!getPlatformMetadata(data.session.user)) {
		throw redirect({ to: "/waitlist" })
	} else if (!isAdmin(data.session.user)) {
		toast("Unauthorized")
		throw redirect({ to: "/dashboard" })
	}

	if (isDebug) {
		console.log("User", data.session.user)
	}

	return {
		user: data.session.user,
		token: data.session.access_token,
		refresh: refreshSession,
	}
}

export const rolePageLoader =
	(allowedRoles: UserRole[], redirectTo = "/dashboard") =>
	async () => {
		const { data, error } = await getAuthSession()
		if (error) {
			console.error("Auth getSession Error", { error })
		}

		if (!data.session) {
			throw redirect({ to: "/auth" })
		} else if (!getPlatformMetadata(data.session.user)) {
			throw redirect({ to: "/waitlist" })
		}

		const role = getUserRole(data.session.user)
		if (!role || !allowedRoles.includes(role)) {
			toast("Unauthorized")
			throw redirect({ to: redirectTo })
		}

		if (isDebug) {
			console.log("User", data.session.user)
		}

		return {
			user: data.session.user,
			role,
			token: data.session.access_token,
			refresh: refreshSession,
		}
	}

export const authPageLoaderAuthenticated =
	(redirectAuthorized?: string) => async () => {
		const { data, error } = await getAuthSession()
		if (error) {
			console.error("Auth getSession Error", { error })
		}

		if (!data.session) {
			throw redirect({ to: "/auth" })
		} else if (getPlatformMetadata(data.session.user)) {
			if (redirectAuthorized) {
				throw redirect({ to: redirectAuthorized })
			}
		}

		if (isDebug) {
			console.log("User", data.session.user)
		}

		return {
			user: data.session.user,
			token: data.session.access_token,
			refresh: refreshSession,
		}
	}

/*
 * helpers
 */
async function getAuthSession() {
	if (
		typeof window !== "undefined" &&
		new URLSearchParams(window.location.search).get("sessionRefresh") === "true"
	) {
		return refreshSession()
	}
	const session = await supabase.auth.getSession()
	if (session.data.session?.user && isOnWaitlist(session.data.session.user)) {
		return refreshSession()
	}

	return session
}

async function refreshSession() {
	const { data, error } = await supabase.auth.refreshSession()
	if (error) {
		console.error("Error refreshing session", { error })
		toast("Error refreshing session")
	}
	return { data: { session: data.session }, error }
}
